import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../../services/api';
import PasswordInput from '../common/PasswordInput';
import { Building2 } from 'lucide-react';

const SetupPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefPhone = searchParams.get('phone') || '';
  const prefEmail = searchParams.get('email') || '';
  const prefOtp = searchParams.get('otp') || '';

  const [identifier, setIdentifier] = useState(prefEmail || prefPhone);
  const [otp, setOtp] = useState(prefOtp);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const identifierLabel = useMemo(() => (identifier.includes('@') ? 'Email' : 'Phone'), [identifier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !otp.trim() || !password.trim()) {
      toast.error('Please fill all required fields');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const payload = identifier.includes('@')
        ? { email: identifier.trim(), otp: otp.trim(), password }
        : { phone: identifier.trim(), otp: otp.trim(), password };
      await authAPI.setupPassword(payload);
      toast.success('Password set successfully. You can now login with email/phone + password.');
      navigate('/');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to set password';
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='h-full flex flex-col items-center justify-center p-8 animate-slide-up'>
       <div className='text-center mb-6'>
                  <div className='w-24 h-24 bg-gradient-to-br from-[#FFC107] to-[#FF9800] rounded-3xl flex items-center justify-center mb-6 mx-auto'>
                    <Building2 className='w-12 h-12 text-white' />
                  </div>
                  <h1 className='text-4xl font-black italic mb-2 tracking-tighter text-white uppercase'>cssRMS</h1>
                  <p className='text-[#FFC107] text-sm font-bold uppercase tracking-[0.3em] mb-2'>Revenue Management System</p>
                  <p className='text-xs text-white/40'>Harvard RMetaverse Project</p>
                </div>
      <div className='w-full max-w-md'>
        <button
          type='button'
          onClick={() => navigate('/')}
          className='text-xs text-white/60 hover:text-white/90 underline mb-4'>
          Back to login
        </button>

        <h1 className='text-2xl font-black text-white mb-2 uppercase'>Setup Password</h1>
        <p className='text-sm text-white/60 mb-6'>Use the OTP you received to set your password.</p>

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label className='block text-xs font-bold uppercase text-white/60 mb-2'>Email or phone</label>
            <input
              type='text'
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className='w-full p-4 bg-white/5 border border-white/10 rounded-2xl outline-none text-white'
              placeholder='agent@example.com or +233...'
            />
            <p className='text-xs text-white/40 mt-1'>Detected: {identifierLabel}</p>
          </div>

          <div>
            <label className='block text-xs font-bold uppercase text-white/60 mb-2'>OTP</label>
            <input
              type='text'
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className='w-full p-4 bg-white/5 border border-white/10 rounded-2xl outline-none text-white'
              placeholder='123456'
              maxLength={6}
            />
          </div>

          <div>
            <label className='block text-xs font-bold uppercase text-white/60 mb-2'>New password</label>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder='At least 8 characters' required />
          </div>

          <div>
            <label className='block text-xs font-bold uppercase text-white/60 mb-2'>Confirm password</label>
            <PasswordInput value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder='Re-enter password' required />
          </div>

          <button
            type='submit'
            disabled={loading}
            className='w-full py-4 rounded-2xl btn-gold font-black uppercase text-sm disabled:opacity-50'>
            {loading ? 'Saving...' : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SetupPassword;
